import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';

import { AuthService } from './auth.service';

/** Ruta (top-level, fuera del layout) del cambio de contraseña obligatorio. */
const PASSWORD_CHANGE_ROUTE = '/cambiar-password';

/**
 * Bloquea acceso a rutas autenticadas si no hay sesión activa, y fuerza el
 * cambio de contraseña cuando el usuario tiene `debeCambiarPassword`.
 *
 * Se usa como `canActivate` (raíz del layout) y `canActivateChild` (para que
 * corra en CADA navegación interna, no sólo al montar el layout) — por eso el
 * tipo intersección.
 *
 * `isAuthenticated()` considera tanto la presencia del user como el `exp` del
 * JWT. Si había user pero el token venció, llamamos `logout()` para limpiar el
 * localStorage antes del redirect — evita que el sidebar y los links queden
 * "zombi" después de que la sesión expira.
 */
export const authGuard: CanActivateFn & CanActivateChildFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) {
    // Si llegamos acá pero todavía hay un user "fantasma" en memoria/storage,
    // forzar limpieza. Pasamos `false` para no doble-navegar (lo hacemos acá).
    if (auth.user() !== null) {
      auth.logout(false);
    }
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  // Cambio de contraseña obligatorio: mientras el flag esté puesto, ninguna
  // ruta autenticada es navegable salvo la propia pantalla de cambio.
  if (auth.user()?.debeCambiarPassword && !state.url.startsWith(PASSWORD_CHANGE_ROUTE)) {
    router.navigate([PASSWORD_CHANGE_ROUTE]);
    return false;
  }

  return true;
};
